import React, { useState } from 'react';
import axios from 'axios';
import { MessageSquare, Euro, Send, Loader2, TrendingDown, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const CounterOfferModal = ({
  isOpen,
  onClose,
  offer,        // The original offer from property owner
  onSuccess     // Callback when counter-offer is created
}) => {
  const [counterPrice, setCounterPrice] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Calculate suggestion (10-20% less than original)
  const suggestedPrice = offer?.offered_price ? Math.round(offer.offered_price * 0.85) : 0;
  const minPrice = offer?.offered_price ? Math.round(offer.offered_price * 0.5) : 0;

  const handleSubmit = async () => {
    const price = parseFloat(counterPrice);
    
    if (!price || price <= 0) {
      toast.error('Inserisci un prezzo valido');
      return;
    }

    if (price >= offer.offered_price) {
      toast.error('La contro-offerta deve essere inferiore al prezzo offerto');
      return;
    }

    if (price < minPrice) {
      toast.error(`Il prezzo minimo accettabile è €${minPrice}`);
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/counter-offers`,
        {
          offer_id: offer.id,
          counter_price: price,
          message: message.trim() || null
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success('🎯 Contro-offerta inviata! Il proprietario verrà notificato.');
      onSuccess?.(response.data);
      onClose();
      
      // Reset form
      setCounterPrice('');
      setMessage('');
    } catch (error) {
      console.error('Error creating counter-offer:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'invio della contro-offerta');
    } finally {
      setLoading(false);
    }
  };

  const priceDiff = counterPrice ? (offer?.offered_price - parseFloat(counterPrice)) : 0;
  const discountPercent = counterPrice && offer?.offered_price 
    ? ((priceDiff / offer.offered_price) * 100).toFixed(1)
    : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-purple-600" />
            Fai una Contro-Offerta
          </DialogTitle>
          <DialogDescription>
            Proponi un prezzo diverso al proprietario
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Original Offer Info */}
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-sm text-slate-600 mb-1">Offerta originale di</p>
            <p className="font-semibold text-slate-900">{offer?.property_name}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge className="bg-blue-100 text-blue-800 text-lg px-3 py-1">
                €{offer?.offered_price}/notte
              </Badge>
              {offer?.room_description && (
                <span className="text-sm text-slate-600">{offer.room_description}</span>
              )}
            </div>
          </div>

          {/* Counter Price Input */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">
              La tua contro-offerta (€/notte)
            </label>
            <div className="relative">
              <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="number"
                placeholder={`Suggerito: €${suggestedPrice}`}
                value={counterPrice}
                onChange={(e) => setCounterPrice(e.target.value)}
                className="pl-10 h-14 text-xl font-bold"
                min={minPrice}
                max={offer?.offered_price - 1}
              />
            </div>
            
            {/* Quick suggestion buttons */}
            <div className="flex gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCounterPrice(Math.round(offer?.offered_price * 0.9).toString())}
                className="text-xs"
              >
                -10% (€{Math.round(offer?.offered_price * 0.9)})
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCounterPrice(Math.round(offer?.offered_price * 0.85).toString())}
                className="text-xs"
              >
                -15% (€{Math.round(offer?.offered_price * 0.85)})
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCounterPrice(Math.round(offer?.offered_price * 0.8).toString())}
                className="text-xs"
              >
                -20% (€{Math.round(offer?.offered_price * 0.8)})
              </Button>
            </div>
          </div>

          {/* Savings Calculator */}
          {counterPrice && parseFloat(counterPrice) > 0 && parseFloat(counterPrice) < offer?.offered_price && (
            <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
              <div className="flex items-center gap-2 mb-2">
                <Calculator className="w-5 h-5 text-emerald-600" />
                <span className="font-semibold text-emerald-900">Riepilogo</span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-600">Risparmio per notte</p>
                  <p className="font-bold text-emerald-700 text-lg flex items-center gap-1">
                    <TrendingDown className="w-4 h-4" />
                    €{priceDiff.toFixed(2)} (-{discountPercent}%)
                  </p>
                </div>
                <div>
                  <p className="text-slate-600">Il tuo prezzo</p>
                  <p className="font-bold text-emerald-700 text-lg">
                    €{parseFloat(counterPrice).toFixed(2)}/notte
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Message */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">
              Messaggio (opzionale)
            </label>
            <Textarea
              placeholder="Es: Sarei interessato ma il mio budget è limitato. Possiamo trovare un accordo?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleSubmit}
              disabled={loading || !counterPrice || parseFloat(counterPrice) >= offer?.offered_price}
              className="w-full h-12 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Invio in corso...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Invia Contro-Offerta €{counterPrice || '0'}
                </>
              )}
            </Button>
            
            <Button
              onClick={onClose}
              variant="ghost"
              disabled={loading}
              className="w-full"
            >
              Annulla
            </Button>
          </div>

          {/* Info */}
          <p className="text-xs text-center text-slate-500">
            Il proprietario riceverà la tua proposta e potrà accettarla o rifiutarla.
            <br />
            Se accettata, potrai procedere al pagamento al nuovo prezzo concordato.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CounterOfferModal;
